import { OmniaApiConnection } from "../api/apiConnection"
import { OmniaApiState } from "../api/apiState"
import { camelCaseKeys, CamelCaseObjectKeys } from "../case"
import { OmniaConfig } from "../config"
import { CropYear } from "../cropYear"
import { getBusinessList, getHoldingFilter, HoldingUser, IBusiness, IHolding } from "../holding"
import { ICustomBasemap } from "../map"
import { ApiReadWritePermissions, HoldingUserRole } from "../permissions/holding/api"
import { getHoldingPermissions } from "../permissions/holding/holding"
import { convertResourceAccessRestrictionsFromApi, ResourceAccessRestrictions } from "../permissions/holding/accessRestrictions"
import { UserPermissions } from "../permissions/types"
import { getDefaultPermissions } from "../permissions/user/default"
import { getUserPermissions, setupSatelliteOptions } from "../permissions/user/user"
import { INewCroppingYear, IOmniaHttpService, IOmniaSession, IOmniaSettings, IOmnniaSessionState, LocaleString, LoginCredentials, TermsAndConditionsStatus } from "../types"
import { IOmniaUser, RoleId } from "../user"
import { UsersApi } from "../user/api"
import { isArrayEmpty } from "../utils/array"
import { isFunction, isObject, isUndefinedOrNull } from "../utils/utils"
import { SessionApi } from "./api"
import { getUserBusiness } from "./business"
import { parseInvite } from "./invite"
import { createGlobalSettings } from "./settings/global"
import { createLocalSettings } from "./settings/local"
import { HoldingSelectionResult, SessionInfo } from "./types"
import { getUserRole } from "./utils"
import { omniaResolve } from "../promises"

const accessTokenKey = "accessToken"

/** @internal */
export class SessionService implements IOmniaSession {

    private _init: boolean
    private _reloading: boolean = false

    private _sessionStore: IOmniaSettings

    private _localSettings = createLocalSettings()
    private _globalSettings = createGlobalSettings({ holdingId: 0, userId: 0, settings: [], save: (h, u, k, v) => this.api.setUserSetting(h, u, k, v) })

    private _currentUser: IOmniaUser
    private _currentUserRole: RoleId
    private _userBusiness: IBusiness

    private _sessionId: string

    private _loginLinkToken: string
    private _loginLinkError: string
    private _loginLinkUser: string
    private _restrictedLogin = false

    private resetPassword = {
        reset: false,
        email: undefined as string
    }

    public _invite: IOmniaSession["invite"]

    private _userPermissions: UserPermissions
    private _permissions: UserPermissions
    private _accessRestrictions: ResourceAccessRestrictions

    private _fixedHoldingList = false
    private _holdings: IHolding[]

    private _croppingYears: CropYear[]
    private _bingKey: string

    private _holding: IHolding
    private _holdingUsers: HoldingUser[]
    private _customBasemaps: ICustomBasemap[]
    private _holdingRole: HoldingUserRole

    public readonly holdingTimeZone = "Europe/London"

    private _tcs: TermsAndConditionsStatus = {
        mustAccept: false
    }

    private _yearId: number
    private _cropYear: CropYear

    private _readOnly: boolean

    private _lastLoginHolding: IHolding
    private _lastLoginYear: number

    private _apiConnection: OmniaApiConnection




    static $inject = [
        "$q",
        "$window",
        "$location",
        "OmniaHttpService",
        "omniaSessionState",
        "OmniaApiState",
        "omniaConfig"]

    constructor(
        private $q: ng.IQService,
        private $window: ng.IWindowService,
        private $location: ng.ILocationService,
        public readonly http: IOmniaHttpService,
        public readonly sessionState: IOmnniaSessionState,
        public readonly apiState: OmniaApiState,
        public readonly config: OmniaConfig,
        public readonly production = config.production,
        private api = new SessionApi(http),
    ) {

        this._init = false
        this._sessionStore = this.getSettings("session")

        this.loginAuto()

    }

    isApiRefreshPending(): boolean {
        return this.apiState.isApiRefreshPending()
    }

    getApiVersion(): number {
        return this.apiState.getVersion()
    }

    updateSession() {
        return this.http.updateSession()
    }

    hasApiConnection() {
        return this.apiConnection?.isOpen()
    }

    setClientSate(state: any): PromiseLike<any> {

        this.apiState.setClientState(state)

        this.apiConnection.sendClientState(state)
        return omniaResolve()

    }

    updateUser(userUpdated: IOmniaUser): void {
        this._currentUser = userUpdated
    }

    login(credentials: LoginCredentials): ng.IPromise<any> {

        this.sessionState.state = "loginInProgress"
        this._loginLinkError = undefined
        this._loginLinkUser = undefined

        return this.http.login(credentials)
            .then(r => r.success
                ? this.setupSession(r.token, r.restricted, false).then(user => ({ user, error: undefined }))
                : { user: undefined, error: r.errorMessage }
            )

    }

    private loginAuto() {

        const { login, signup, resetPassword, token, invite } = this.$location.search()

        this._invite = parseInvite(invite)

        if (signup) {
            return this.loginSignup(signup)
        }

        if (login) {
            return this.loginUsingTempToken(login)
        }

        if (token) {
            return this.loginUsingToken(token, false)
        }

        /**
         * If a ?resetPassword flag was found on the query string,
         * then the process stops here - we just set the local @_resetPassword
         * flag, which will be used later during the login process
         */
        if (resetPassword) {
            this.resetPassword = {
                reset: true,
                email: undefined
            }
            this._init = true
            return
        }

        /**
         * If login from a invite link, ignore the stored token to force an explicit login
         */
        if (this._invite) {
            this._init = true
            return
        }

        const reload = !!sessionStorage.getItem(accessTokenKey)

        const accessToken = this.getAccessToken()

        if (accessToken) {
            return this.loginUsingToken(accessToken, reload)
        }

        this._init = true

    }

    /**
     * Automatic login through the last JWT token stored locally
     *
     * @param accessToken
     */
    private loginUsingToken(accessToken: string, reload: boolean) {

        return this.http.loginAuto(accessToken)
            .then(r => {

                if (r && r.success) {
                    return this.setupSession(accessToken, r.restricted, reload)
                }

            })
            .finally(() => {
                this._init = true
            })

    }


    /**
     * Automatic login through a signup request token
     *
     * @param token
     */
    private loginSignup(token: string) {

        const s = (token || "").split(":")
        const [key, email] = s

        const redirectError = () => location.replace(`/start/signup/error.html?email=${encodeURIComponent(email || "")}`)


        if (key && email) {

            const url = `/api/signup/v1/validate/?key=${encodeURIComponent(key)}&email=${encodeURIComponent(email)}`

            return this.http.post(url)
                .then(r => this.loginUsingTempToken(r.data))
                .catch(() => {

                    if (this._loginLinkToken) {
                        this._loginLinkError = "Invalid signup request" as LocaleString
                        this._loginLinkUser = email
                        this._init = true
                        this.$location.url("/")
                    }
                    else {
                        redirectError()
                    }

                })

        }

        redirectError()

    }

    /**
     * Automatic login through a temporary login token passed on the query string
     *
     * @param token
     */
    private loginUsingTempToken(token: string) {

        this._loginLinkToken = token

        return this.login({ token })
            .then(r => {

                if (r && r.error) {
                    this._loginLinkError = r.error
                    this._loginLinkUser = JSON.parse(atob(token))["Email"]
                }

            })
            .finally(() => {
                this._init = true
                this.$location.url("/")
            })


    }

    private getAccessToken() {
        return sessionStorage.getItem(accessTokenKey) || this._sessionStore.get(accessTokenKey)
    }

    private saveAccessToken(token: string) {

        this._sessionStore.set(accessTokenKey, token)

        if (isUndefinedOrNull(token)) {
            sessionStorage.removeItem(accessTokenKey)
        }
        else {
            sessionStorage.setItem(accessTokenKey, token)
        }

    }

    private setupSession(accessToken: string, restricted: boolean, reload: boolean): ng.IPromise<any> {

        let user: SessionInfo["User"]
        let initialHoldingId: number
        let initialYear: number

        this._restrictedLogin = restricted

        let auto = !!reload

        const invite = this._invite

        if (!invite) {
            initialHoldingId = this._sessionStore.get("holdingId")
            initialYear = this._sessionStore.get("yearId")
        }

        return this.api.startSession(initialHoldingId, invite?.id)
            .then(r => {

                this.saveAccessToken(accessToken)

                user = r.User

                this._croppingYears = r.CroppingYears.map(y => new CropYear(y))
                this._bingKey = r.BingMaps ? r.BingMaps.Key : null

                this._holdings = r.Holdings || []
                this._fixedHoldingList = r.FixedHoldingList || false

                if (isObject(r.InitialHolding) && initialYear) {
                    this._lastLoginHolding = r.InitialHolding
                    this._lastLoginYear = initialYear
                }

                if (isObject(r.InitialHolding) && invite) {
                    this._lastLoginHolding = r.InitialHolding
                }



                this._tcs = (!r.MustAcceptTermsAndConditions)
                    ? {
                        mustAccept: false
                    }
                    : {
                        mustAccept: true,
                        tcs: camelCaseKeys(r.TermsAndConditions)
                    }

                return r

            })
            .then(() => {

                if (!auto) {
                    auto = user?.Properties?.AutoSelectContext
                }

                this.setCurrentUser(user)

                if (auto) {
                    this.selectHolding(this._lastLoginHolding, this._lastLoginYear)
                }

                this.sessionState.state = "loggedIn"
                this._loginLinkToken = undefined

            })

    }

    get globalSettings() {
        return this._globalSettings
    }

    logout() {

        this._apiConnection?.close()

        this.http.logout().then(() => {

            this._loginLinkToken = undefined
            this._loginLinkError = undefined
            this._loginLinkUser = undefined

            this._reloading = true

            this.setCurrentUser(null)

            this.saveAccessToken(null)

            this.$window.location.reload()

        })

    }

    private setCurrentUser(user: IOmniaUser) {

        this._currentUser = user
        const role = this._currentUserRole = getUserRole(user)

        this._userPermissions = getUserPermissions(user?.Permissions, role)

    }

    resetContext() {

        this._sessionStore.set("holdingId", undefined)
        this._sessionStore.set("yearId", undefined)

        this.$window.location.reload()

    }

    setResetPassword(reset: boolean, email: string): void {
        this.resetPassword = { reset, email: reset ? email : undefined }
    }

    get isInitialized(): boolean {
        return this._init
    }

    get currentUser() {
        return this._currentUser
    }

    get termsAndConditions() {
        return this._tcs
    }

    acceptTcs(version: string, send: boolean): PromiseLike<boolean> {

        const api = new UsersApi(this.http)

        return api.acceptTcs(version, send).then(ok => {

            if (ok) {
                this._tcs = { mustAccept: false }
            }

            return ok

        })

    }

    get currentUserRole() {
        return this._currentUserRole
    }

    get currentHoldingUserRole() {
        return this._holdingRole
    }

    get sessionId() {
        return this._sessionId
    }

    isReadOnly(selector?: (p: UserPermissions) => Readonly<CamelCaseObjectKeys<ApiReadWritePermissions>>): boolean {

        if (this._readOnly) {
            return true
        }

        if (selector) {
            const p = selector(this.permissions)
            return p ? (!p.write) : this.fixedHoldingList
        }

        return false

    }

    get apiConnection() {
        return this._apiConnection
    }

    get userBusiness() {
        return this._userBusiness
    }

    get permissions() {
        return this._permissions ?? this._userPermissions ?? getDefaultPermissions(this._currentUserRole)
    }

    get accessRestrictions() {
        return this._accessRestrictions
    }

    get reloading() {
        return this._reloading
    }

    get autoSignup() {
        const { _init, _currentUser: user } = this
        return _init && user && user.AutoSignupInProgress
    }

    get passwordResetRequested() {
        const { _currentUser: user } = this
        return user && user.PasswordResetRequested
    }
    get loginLinkToken() {
        return this._loginLinkToken
    }

    get loginLinkError() {
        return this._loginLinkError
    }

    get loginLinkUser() {
        return this._loginLinkUser
    }

    get restrictedLogin() {
        return this._restrictedLogin
    }

    get invite() {
        return this._invite
    }

    get shouldResetPassword() {
        return this.resetPassword
    }

    get holding() {
        return this._holding
    }

    get holdingId() {
        return this._holding?.HoldingId
    }

    get holdingRegion() {
        return this._holding?.Region
    }

    get yearId() {
        return this._yearId
    }

    get cropYear() {
        return this._cropYear
    }

    get holdingUsers() {
        return this._holdingUsers
    }

    get holdingSessions() {
        return this.apiState.sessions
    }

    get holdingYears() {
        return this._holding?.CropYears
    }

    get business() {
        return this._userBusiness
    }

    openCroppingYear(model: INewCroppingYear): ng.IPromise<boolean> {

        return this.api.openCroppingYear(model).then(ok => {

            if (ok) {
                this.registerNewCroppingYear(model.YearId)
                return this.selectHolding(this.holding, model.YearId).then(() => true)
            }

            return ok

        })
    }

    /**
     * TODO: this is far from ideal - we should rather requery the API to get an updated list
     * of cropping years
     */
    private registerNewCroppingYear(year: number) {

        const { holding } = this
        const years = holding.CropYears

        if (years.every(y => y.YearId !== year)) {

            years.push({
                YearId: year,
            })

            holding.CropYears = [...years]

        }

    }

    get fixedHoldingList() {
        return this._fixedHoldingList
    }

    hasFixedHoldings() {
        return !isArrayEmpty(this._holdings)
    }

    getHoldings(): IHolding[] {
        return [...this._holdings || []]
    }

    queryHoldings(query: string, limit?: number): PromiseLike<IHolding[]> {

        if (this._fixedHoldingList) {
            const filter = getHoldingFilter(query)
            return this.$q.resolve(this._holdings.filter(filter))
        }

        if (!query) {
            return omniaResolve([])
        }

        return this.api.queryHoldings(query, limit)

    }

    getCroppingYears() {
        return this._croppingYears
    }

    get lastLoginHolding(): IHolding {
        return this._lastLoginHolding
    }

    get lastLoginYear(): number {
        return this._lastLoginYear
    }

    get debugMode(): boolean {
        return this._currentUser && this._currentUser.Properties && this._currentUser.Properties.Debug
    }

    getUserProperty(name: string) {
        if (this.currentUser && this._currentUser.Properties) {
            return this._currentUser.Properties[name]
        }
    }


    get bingKey(): string {
        return this._bingKey
    }

    get customBasemaps() {
        return this._customBasemaps || []
    }


    public requestFullRefresh(newCroppingYear?: number) {

        if (newCroppingYear) {
            this.registerNewCroppingYear(newCroppingYear)
        }

        this.apiState.recordChange({ source: "refresh-all", local: true })

    }

    public refreshHoldings() {

        if (!this._fixedHoldingList) {

            if (!this.holdingId) {
                return omniaResolve()
            }

            return this.api.getCurrentHolding().then(h => {
                this._holding = setupHolding(h)
            })

        }

        return this.api.getUserHoldings().then((result: IHolding[]) => {

            this._holdings = result

            if (this._holding) {
                const h = this._holdings.find(h => h.HoldingId === this._holding.HoldingId)
                this._holding = setupHolding(h)
            }

        })

    }

    public selectHolding(holding: IHolding, yearId?: number): ng.IPromise<boolean> {

        let reload: boolean = false

        if (!isObject(holding)) {
            return this.$q.reject()
        }

        if (!holding.CropYears.map(c => c.YearId).find(y => y == yearId)) {
            return this.$q.reject()
        }

        const holdingId = holding.HoldingId

        if (this._holding && (this._holding.HoldingId != holdingId)) {
            reload = true
        }

        const reuseSession = holdingId && (Number(sessionStorage.getItem("holdingId")) == holdingId)

        const clientState = {
            permissions: this._permissions
        }

        return this.http.selectHolding(holdingId, reuseSession, clientState)
            .then((h: HoldingSelectionResult) => {

                if (!h.Holding) {
                    return false
                }

                sessionStorage.setItem("holdingId", holdingId.toString())
                yearId = yearId || (holding.CropYears[holding.CropYears.length - 1]).YearId

                this._sessionStore.set("holdingId", holdingId)
                this._sessionStore.set("yearId", yearId)

                this._customBasemaps = h.CustomBasemaps

                this.saveAccessToken(h.Token)

                if (reload) {
                    this.reloadToNewContext()
                    return false
                }

                this._holding = setupHolding(h.Holding)
                this._holdingUsers = h.Users
                this._holdingRole = h.Role

                this._yearId = yearId
                this._cropYear = this._croppingYears.find(y => y.YearId == this.yearId) || new CropYear(this.yearId)

                this._sessionId = h.SessionId
                this._readOnly = h.ReadOnly

                if (h.UserPermissions) {
                    this._userPermissions = getUserPermissions(h.UserPermissions, this._currentUserRole)
                }

                const userPermissions = this._userPermissions
                const holdingPermissions = camelCaseKeys(h.Permissions)

                this._permissions = getHoldingPermissions(this._currentUserRole, this._holding, userPermissions, holdingPermissions)
                this._accessRestrictions = convertResourceAccessRestrictionsFromApi(h.AccessRestrictions)

                this._userBusiness = getUserBusiness(this._currentUser)

                this._globalSettings = createGlobalSettings({
                    holdingId,
                    userId: this.currentUser.Id,
                    settings: h.Settings,
                    save: (h, u, k, v) => this.api.setUserSetting(h, u, k, v)
                })

                this.sessionState.handleUnauthorized = true

                this.apiState.currentSessionId = h.SessionId
                this.apiState.updateSessions(h.Sessions)
                this.apiState.setupEvents(h.Events?.Events, h.Events?.Users)

                this._apiConnection = this._apiConnection
                    ?? new OmniaApiConnection(
                        this.http.getApiBaseUrl(),
                        this.apiState,
                        () => !this.sessionState.unauthorized,
                        {
                            waitOnMasterDataUpdateNotifications: !this.debugMode
                        })

                this._apiConnection.open(h.SessionId, h.Token)


                return true

            })

    }

    selectYear(year: number) {

        if (year == this.yearId) {
            return false
        }

        const { holding } = this

        if (!holding) {
            return false
        }

        if (!holding.CropYears.some(y => y.YearId == year)) {
            return false
        }

        this._yearId = year
        this._cropYear = this._croppingYears.find(y => y.YearId == year) || new CropYear(this.yearId)
        this._sessionStore.set("yearId", year)

        return true

    }

    private reloadToNewContext() {

        this._reloading = true

        var loc = this.$location

        var url = loc.protocol() + "://" + loc.host()

        var port = loc.port()
        if (port != 80) url += ":" + port

        url += "/#/dashboard"

        this.$window.location.href = url
        this.$window.location.reload()

    }

    getSettings(namespace: string): IOmniaSettings {

        return this._localSettings.get(namespace)

    }

    disableBlockUI(run: () => PromiseLike<any>) {

        this.sessionState.disableBlockUI = true

        const then = () => this.sessionState.disableBlockUI = false

        return run().then(then, then)

    }

    private _search: URLSearchParams

    getSeachParam(key: string) {

        if (isFunction(URLSearchParams)) {
            this._search = this._search || new URLSearchParams(location.search.substring(1))
            return this._search.get(key)
        }

    }

}


function setupHolding(holding: IHolding) {

    if (holding.BusinessId && (!holding.Business)) {
        holding.Business = getBusinessList().find(h => h.BusinessId == holding.BusinessId)
    }

    holding.Satellite = setupSatelliteOptions(holding.Satellite)

    return holding

}
